import * as z from 'zod'

import {
    KratosAal,
    KratosAuthMethod,
    KratosContinueWithAction,
    KratosErrorID,
    KratosFlowType,
    KratosInputType,
    KratosNodeGroup,
    KratosNodeType,
    KratosOutcome,
} from '@/enums/kratos'
import type {
    IKratosError,
    IKratosErrorResponse,
    IKratosFlow,
    IKratosFlowSuccess,
    IKratosSession,
    IKratosUiNode,
    IKratosUiText,
} from '@/types/Kratos'

/**
 * The ID portal's conversation with the Kratos public API — pure functions only (no Vue, no
 * Pinia, no DOM), so every branch is unit-testable.
 *
 * Kratos is mounted at `/kratos` on the ID host; the edge strips the prefix. Flows
 * are CREATED by a full-page navigation to `/kratos/self-service/<flow>/browser` (Kratos then
 * redirects to our UI with `?flow=<id>`), FETCHED with `GET /self-service/<flow>/flows?id=` and
 * SUBMITTED as JSON to `flow.ui.action` — always rewritten to the same-origin path, which keeps
 * Kratos's host-bound CSRF cookie valid. Login submissions pass through auth-api's lockout gate
 * at the edge, which may answer 429.
 */

export const KRATOS_BASE_PATH = '/kratos'

export type KratosResponse<T> =
    | { kind: KratosOutcome.Ok;                            status: number; data: T }
    | { kind: KratosOutcome.Validation;                    status: number; data: IKratosFlow }
    | { kind: KratosOutcome.BrowserLocationChangeRequired; status: number; redirectTo?: string }
    | { kind: KratosOutcome.SessionRefreshRequired;        status: number; redirectTo?: string }
    | { kind: KratosOutcome.SessionAal2Required;           status: number; redirectTo?: string }
    | { kind: KratosOutcome.FlowExpired;                   status: number; useFlowId?: string }
    | { kind: KratosOutcome.SessionAlreadyAvailable;       status: number }
    | { kind: KratosOutcome.TooManyAttempts;               status: number }
    | { kind: KratosOutcome.NoSession;                     status: number }
    | { kind: KratosOutcome.Error;                         status: number; error?: IKratosError }

export const withQuery = (path: string, query: Record<string, string | undefined>): string => {
    const params = new URLSearchParams()

    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== '') params.set(key, value)
    })

    const search = params.toString()
    return search ? `${path}?${search}` : path
}

/** Any Kratos-minted URL as a path relative to the `/kratos` base the fetch store requests. */
export const toKratosPath = (url: string): string => {
    const parsed = new URL(url, 'http://flow.invalid')

    let path = parsed.pathname
    if (path === KRATOS_BASE_PATH) path = '/'
    else if (path.startsWith(`${KRATOS_BASE_PATH}/`)) path = path.slice(KRATOS_BASE_PATH.length)

    return `${path}${parsed.search}`
}

/** The same rewrite, as the same-origin path the BROWSER navigates to (prefix back on). */
export const toKratosBrowserPath = (url: string): string => `${KRATOS_BASE_PATH}${toKratosPath(url)}`

/** Full-page navigation target that makes Kratos create a browser flow. */
export const kratosFlowBrowserUrl = (type: KratosFlowType, query: Record<string, string | undefined> = {}): string =>
    withQuery(`${KRATOS_BASE_PATH}/self-service/${type}/browser`, query)

/** Request path (relative to `/kratos`) that reads an existing flow. */
export const kratosFlowFetchPath = (type: KratosFlowType, id: string): string =>
    withQuery(`/self-service/${type}/flows`, { id })

/**
 * Request path (relative to `/kratos`) that creates a settings flow as JSON. Used where the
 * person already holds a session and stays on our page (settings, the invite enrolment steps).
 */
export const KRATOS_SETTINGS_CREATE_PATH = '/self-service/settings/browser'
export const KRATOS_WHOAMI_PATH          = '/sessions/whoami'
export const KRATOS_LOGOUT_PATH          = '/self-service/logout/browser'

export const kratosErrorPath = (id: string): string => withQuery('/self-service/errors', { id })

/**
 * `/login?login_challenge=<c>` with no flow: Kratos is asked for a login flow whose
 * `return_to` is the bridge, so a successful login lands back on `/oauth/login`.
 */
export const loginChallengeEntryUrl = (origin: string, challenge: string): string =>
    kratosFlowBrowserUrl(KratosFlowType.Login, {
        return_to: `${origin}/oauth/login?login_challenge=${encodeURIComponent(challenge)}`,
    })

/** Where `/settings?setup=security` continues to once TOTP is enrolled. */
export const oauthLoginUrl = (challenge: string): string =>
    `/oauth/login?login_challenge=${encodeURIComponent(challenge)}`

/** Query that re-creates a login flow equivalent to `flow` (expired flow, CSRF failure). */
export const loginRestartQuery = (flow?: IKratosFlow): Record<string, string | undefined> => ({
    return_to: flow?.return_to || undefined,
    refresh:   flow?.refresh ? 'true' : undefined,
    aal:       flow?.requested_aal === KratosAal.Aal2 ? KratosAal.Aal2 : undefined,
})

const isFlowPayload = (data: unknown): data is IKratosFlow =>
    !!data && typeof data === 'object' && 'ui' in (data as Record<string, unknown>)

/** Kratos identifies each special case by `error.id`; the status is the fallback. */
export const classifyKratosResponse = <T>(status: number, data: unknown): KratosResponse<T> => {
    if (status >= 200 && status < 300) return { kind: KratosOutcome.Ok, status, data: data as T }

    if (status === 429) return { kind: KratosOutcome.TooManyAttempts, status }

    const payload    = (data && typeof data === 'object' ? data : {}) as IKratosErrorResponse
    const redirectTo = payload.redirect_browser_to

    switch (payload.error?.id) {
        case KratosErrorID.BrowserLocationChangeRequired:
            return { kind: KratosOutcome.BrowserLocationChangeRequired, status, redirectTo }
        case KratosErrorID.SessionRefreshRequired:
            return { kind: KratosOutcome.SessionRefreshRequired, status, redirectTo }
        case KratosErrorID.SessionAal2Required:
            return { kind: KratosOutcome.SessionAal2Required, status, redirectTo }
        case KratosErrorID.SelfServiceFlowExpired:
        case KratosErrorID.SecurityCsrfViolation:
            return { kind: KratosOutcome.FlowExpired, status, useFlowId: payload.use_flow_id }
        case KratosErrorID.SessionAlreadyAvailable:
            return { kind: KratosOutcome.SessionAlreadyAvailable, status }
    }

    if (status === 400 && isFlowPayload(data)) return { kind: KratosOutcome.Validation, status, data }
    if (status === 401) return { kind: KratosOutcome.NoSession, status }
    if (status === 410) return { kind: KratosOutcome.FlowExpired, status, useFlowId: payload.use_flow_id }
    if (status === 422 && redirectTo) return { kind: KratosOutcome.BrowserLocationChangeRequired, status, redirectTo }

    return { kind: KratosOutcome.Error, status, error: payload.error }
}

/** The `redirect_browser_to` continuation of a successful JSON submission, if any. */
export const kratosContinueRedirect = (payload: unknown): string | undefined => {
    const entries = (payload as IKratosFlowSuccess | undefined)?.continue_with ?? []

    return entries.find((entry) => entry?.action === KratosContinueWithAction.RedirectBrowserTo)?.redirect_browser_to
}

/**
 * The settings flow id out of the 422 a redeemed invite code answers with
 * (`https://<host>/settings?flow=<id>`). Undefined for any other target.
 */
export const settingsFlowIdFromRedirect = (url?: string): string | undefined => {
    if (!url) return undefined

    const parsed = new URL(url, 'http://flow.invalid')
    if (parsed.pathname !== '/settings') return undefined

    return parsed.searchParams.get('flow') || undefined
}

/** Does the session hold a first factor? An invite session holds only `code_recovery`. */
export const sessionHasFirstFactor = (session?: IKratosSession): boolean =>
    (session?.authentication_methods ?? []).some((entry) =>
        entry.method === KratosAuthMethod.Password || entry.method === KratosAuthMethod.Passkey)

// ---------------------------------------------------------------------------------------
// UI nodes — classification on `type` / `attributes.type`, never on a trait name.
// ---------------------------------------------------------------------------------------

export const nodeName = (node: IKratosUiNode): string => node.attributes?.name ?? ''

export const nodeInputType = (node: IKratosUiNode): string =>
    (node.attributes?.type ?? KratosInputType.Text).toLowerCase()

export const nodeLabel = (node: IKratosUiNode): IKratosUiText | undefined => node.meta?.label

export const isInputNode   = (node: IKratosUiNode): boolean => node.type === KratosNodeType.Input
export const isHiddenNode  = (node: IKratosUiNode): boolean => isInputNode(node) && nodeInputType(node) === KratosInputType.Hidden
export const isSubmitNode  = (node: IKratosUiNode): boolean => isInputNode(node) && nodeInputType(node) === KratosInputType.Submit
export const isTriggerNode = (node: IKratosUiNode): boolean => isInputNode(node) && nodeInputType(node) === KratosInputType.Button
export const isScriptNode  = (node: IKratosUiNode): boolean => node.type === KratosNodeType.Script

/**
 * A visible input Kratos added only to carry a message — no label of its own. Kratos does this
 * when it refuses removing the last passkey (`webauthn_remove`): rendering
 * it as a field would show an empty text box, so its messages are shown instead.
 */
export const isMessageOnlyNode = (node: IKratosUiNode): boolean =>
    isInputNode(node) && !isHiddenNode(node) && !isSubmitNode(node) && !isTriggerNode(node) && !nodeLabel(node)

/** An input the person types into. */
export const isFieldNode = (node: IKratosUiNode): boolean =>
    isInputNode(node) && !isHiddenNode(node) && !isSubmitNode(node) && !isTriggerNode(node) && !isMessageOnlyNode(node)

export const findNode = (flow: IKratosFlow | undefined, name: string): IKratosUiNode | undefined =>
    flow?.ui?.nodes?.find((node) => nodeName(node) === name)

export const hasNode = (flow: IKratosFlow | undefined, name: string): boolean => !!findNode(flow, name)

export const csrfToken = (flow?: IKratosFlow): string | undefined => {
    const value = findNode(flow, 'csrf_token')?.attributes?.value

    return typeof value === 'string' && value.length > 0 ? value : undefined
}

/** Every message the flow carries outside a rendered field: flow-wide and message-only nodes. */
export const flowMessages = (flow?: IKratosFlow): IKratosUiText[] => [
    ...(flow?.ui?.messages ?? []),
    ...(flow?.ui?.nodes ?? []).filter(isMessageOnlyNode).flatMap((node) => node.messages ?? []),
]

// ---------------------------------------------------------------------------------------
// State, schema and submission body
// ---------------------------------------------------------------------------------------

export type KratosFlowState = Record<string, unknown>

/** The `name`/`value` of the submit button that was pressed — Kratos's method switch. */
export interface IKratosSubmitPair {
    name:  string
    value: unknown
    group: string
}

export interface IKratosValidationMessages {
    required: string
    email:    string
}

/** Initial form state: one entry per named input, seeded with Kratos's value. */
export const buildFlowState = (nodes: IKratosUiNode[]): KratosFlowState => {
    const state: KratosFlowState = {}

    nodes.filter((node) => isInputNode(node) && nodeName(node) !== '' && !isSubmitNode(node) && !isTriggerNode(node))
        .forEach((node) => { state[nodeName(node)] = node.attributes.value ?? '' })

    return state
}

/**
 * One zod field per typed input. `isActive` is read at validation time, so pressing one method's
 * button never demands another method's required field.
 */
export const buildFlowSchema = (
    nodes:    IKratosUiNode[],
    isActive: (node: IKratosUiNode) => boolean,
    messages: IKratosValidationMessages,
) => {
    const shape: Record<string, z.ZodType> = {}

    nodes.filter((node) => isFieldNode(node) && nodeName(node) !== '').forEach((node) => {
        const filled = z.string().optional().refine(
            (value) => !(node.attributes.required && isActive(node)) || (value ?? '').trim().length > 0,
            { message: messages.required },
        )

        shape[nodeName(node)] = nodeInputType(node) === KratosInputType.Email
            ? filled.refine((value) => !value || z.email().safeParse(value).success, { message: messages.email })
            : filled
    })

    return z.object(shape)
}

/**
 * Writes `value` at a dot path: Kratos names trait inputs `traits.name.first`, a JSON body nests.
 */
export const setValueAtPath = (target: Record<string, unknown>, path: string, value: unknown): void => {
    const segments = path.split('.')
    let cursor = target

    segments.slice(0, -1).forEach((segment) => {
        const next = cursor[segment]
        if (typeof next !== 'object' || next === null) cursor[segment] = {}
        cursor = cursor[segment] as Record<string, unknown>
    })

    cursor[segments[segments.length - 1]] = value
}

/**
 * The JSON body for the pressed button: the `default` group's inputs (CSRF token, login
 * identifier) and the pressed method's own inputs, plus the button's `name`/`value`. A button
 * that is not the `method` switch itself (`totp_unlink`, `passkey_remove`) also sends
 * `method: <its group>`, which is how Kratos picks the strategy.
 */
export const buildFlowBody = (
    nodes:     IKratosUiNode[],
    state:     KratosFlowState,
    submitted: IKratosSubmitPair,
): Record<string, unknown> => {
    const body: Record<string, unknown> = {}

    nodes.forEach((node) => {
        const name = nodeName(node)

        if (!isInputNode(node) || name === '') return
        if (node.attributes.disabled) return
        if (isSubmitNode(node) || isTriggerNode(node) || isMessageOnlyNode(node)) return
        if (node.group !== KratosNodeGroup.Default && node.group !== submitted.group) return

        setValueAtPath(body, name, state[name] ?? '')
    })

    if (submitted.name !== 'method') body.method = submitted.group
    if (submitted.name !== '') setValueAtPath(body, submitted.name, submitted.value)

    return body
}

/** The body of a passkey ceremony result: only what Kratos needs. */
export const buildPasskeyBody = (flow: IKratosFlow, field: string, credential: string): Record<string, unknown> => {
    const token = csrfToken(flow)

    return {
        ...(token ? { csrf_token: token } : {}),
        method:  KratosNodeGroup.Passkey,
        [field]: credential,
    }
}
